import {
  type LoginEmailPasswordMutation,
  type LoginEmailPasswordMutationVariables,
  type MutationHookOptions,
  useLoginEmailPasswordMutation,
} from "@qw/graphql";
import { useAtom } from "jotai";
import { authDataAtom, authTokenValidatedAtom } from "../atoms";
import { useAuthContext } from "./use-auth-context";

type LoginOptions = Omit<
  MutationHookOptions<LoginEmailPasswordMutation, LoginEmailPasswordMutationVariables>,
  "variables"
> & {
  variables: Omit<LoginEmailPasswordMutationVariables, "authClientId"> & { rememberMe: boolean };
};

export function useLoginEmailPassword() {
  const [, setAuthData] = useAtom(authDataAtom);
  const [, setTokenValidated] = useAtom(authTokenValidatedAtom);
  const { authClientId } = useAuthContext();

  const [loginEmailPasswordMutation, result] = useLoginEmailPasswordMutation();

  const loginEmailPassword = (options: LoginOptions) => {
    const { rememberMe, ...variables } = options.variables;

    return loginEmailPasswordMutation({
      ...options,
      variables: { authClientId, ...variables },
      onCompleted: (data) => {
        setAuthData({
          token: data.loginEmailPassword.token,
          shouldRemember: rememberMe ?? false,
        });
        setTokenValidated(true);
        options?.onCompleted?.(data);
      },
      onError: (error) => options?.onError?.(error),
    });
  };

  return [loginEmailPassword, result] as const;
}
